class CountryRedirectPopup extends HTMLElement {
  constructor() {
    super();
    this.storeRegion = this.dataset.thisStoreRegion;
    this.otherStoreUrl = this.trimUrl(this.dataset.otherStoreUrl);
    this.rowStoreUrl = this.trimUrl(this.dataset.rowStoreUrl);
    this.usStoreUrl = this.trimUrl(this.dataset.usStoreUrl);
    this.rowExcludedCountries = (this.dataset.rowExcludedCountries || '')
      .split(',')
      .map(function (code) {
        return code.trim().toUpperCase();
      })
      .filter(Boolean);
    this.countryRedirectUrl = this.dataset.countryRedirectUrl || '';
    this.overlay = this.querySelector('.country-redirect-popup__overlay');
  }

  connectedCallback() {
    if (Shopify.designMode) {
      if (this.dataset.openInDesignMode === 'true') {
       this.show();
      }
      return;
    }

    if (!this.otherStoreUrl && !this.rowStoreUrl && !this.usStoreUrl) return;
    if (this.isBot()) return;

    this.listenForManualCountryChange();

    // A country picked manually in the country selector overrides the dismissed
    // cookie and IP detection, so users who chose the wrong option can switch.
    var manualCountry = this.getCookie('country-redirect-manual');
    if (manualCountry) {
      this.setCookie('country-redirect-manual', '', -1);
      this.handleDetectedCountry(manualCountry, this.getCountryName(manualCountry));
      return;
    }

    if (this.getCookie('country-redirect-dismissed')) return;

    this.detectCountry();
  }

  /**
   * The localization form reloads the page on country change (assets/global.js),
   * so remember the picked country for the next page load. Countries listed in the
   * country-redirects menu navigate straight to another store, so skip those.
   */
  listenForManualCountryChange() {
    document.addEventListener(
      'input',
      function (event) {
        if (!event.target.matches || !event.target.matches('localization-form [name="country_code"]')) return;

        var country = event.target.value;
        if (!country || (window.countryRedirects && window.countryRedirects[country])) return;

        this.setCookie('country-redirect-dismissed', '', -1);
        this.setCookie('country-redirect-manual', country.toUpperCase());
      }.bind(this),
      true
    );
  }

  getCountryName(countryCode) {
    try {
      return new Intl.DisplayNames([document.documentElement.lang || 'en'], { type: 'region' }).of(countryCode);
    } catch (e) {
      return countryCode;
    }
  }

  trimUrl(url) {
    return (url || '').replace(/\/$/, '');
  }

  isBot() {
    return navigator.webdriver || /bot|crawl|spider|slurp|lighthouse/i.test(navigator.userAgent);
  }

  detectCountry() {
    fetch('/browsing_context_suggestions.json')
      .then(function (response) {
        return response.json();
      })
      .then(
        function (data) {
          var country = data && data.detected_values && data.detected_values.country;

          if (country && country.handle) {
            this.handleDetectedCountry(country.handle.toUpperCase(), country.name);
          }
        }.bind(this)
      )
      .catch(function () {
        // Silently fail if geolocation detection is unavailable
      });
  }

  handleDetectedCountry(detectedCountry, countryName) {
    var target = this.getTarget(detectedCountry);
    if (!target) return;

    var content = this.querySelector('.country-redirect-popup__content[data-variant="' + target.variant + '"]');
    if (!content) return;

    this.querySelectorAll('.country-redirect-popup__content').forEach(function (el) {
      el.hidden = el !== content;
    });

    if (target.variant === 'row') {
      this.replaceCountryPlaceholder(content, countryName || detectedCountry);
    }

    this.continueBtn = content.querySelector('.country-redirect-popup__continue');
    this.stayBtn = content.querySelector('.country-redirect-popup__stay');
    this.continueBtn.href = target.url;
    this.attachListeners();
    this.show();
  }

  /**
   * Returns { variant, url } for the store the user should be prompted to visit,
   * or null if no prompt should show.
   * - UK/US stores: prompt the other UK/US store, or the ROW store (only when
   *   row_store_url is set) for users outside the UK and US.
   * - ROW store: prompt the UK or US store for users detected there.
   */
  getTarget(detectedCountry) {
    var currentPath = window.location.pathname + window.location.search;

    if (this.storeRegion === 'ROW') {
      if (detectedCountry === 'GB' && this.otherStoreUrl) {
        return { variant: 'regional', url: this.otherStoreUrl + currentPath };
      }
      if (detectedCountry === 'US' && this.usStoreUrl) {
        return { variant: 'regional', url: this.usStoreUrl + currentPath };
      }
      return null;
    }

    var isOtherRegion =
      (this.storeRegion === 'US' && detectedCountry === 'GB') ||
      (this.storeRegion === 'GB' && detectedCountry === 'US');

    if (isOtherRegion && this.otherStoreUrl) {
      return { variant: 'regional', url: this.countryRedirectUrl || this.otherStoreUrl + currentPath };
    }

    var isRowCountry =
      detectedCountry !== 'GB' &&
      detectedCountry !== 'US' &&
      this.rowExcludedCountries.indexOf(detectedCountry) === -1;

    if (isRowCountry && this.rowStoreUrl) {
      return { variant: 'row', url: this.rowStoreUrl + currentPath };
    }

    return null;
  }

  replaceCountryPlaceholder(content, countryName) {
    var escapedName = document.createElement('span');
    escapedName.textContent = countryName;

    content.querySelectorAll('[data-country-placeholder]').forEach(function (el) {
      el.innerHTML = el.innerHTML.split('[country]').join(escapedName.innerHTML);
    });
  }

  attachListeners() {
    this.stayBtn.addEventListener(
      'click',
      function () {
        this.dismiss();
      }.bind(this)
    );

    this.overlay.addEventListener(
      'click',
      function () {
        this.dismiss();
      }.bind(this)
    );

    // Remember the choice so returning users aren't prompted again
    this.continueBtn.addEventListener(
      'click',
      function () {
        this.setCookie('country-redirect-dismissed', 'true', 30);
      }.bind(this)
    );
  }

  dismiss() {
    this.setCookie('country-redirect-dismissed', 'true', 30);
    this.hide();
  }

  show() {
    this.setAttribute('open', '');
    document.body.style.overflow = 'hidden';
  }

  hide() {
    this.removeAttribute('open');
    document.body.style.overflow = '';
  }

  getCookie(name) {
    var nameEQ = name + '=';
    var ca = document.cookie.split(';');
    for (var i = 0; i < ca.length; i++) {
      var c = ca[i].trim();
      if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length);
    }
    return null;
  }

  setCookie(name, value, days) {
    var expires = '';
    if (days) {
      var date = new Date();
      date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
      expires = '; expires=' + date.toUTCString();
    }
    document.cookie = name + '=' + (value || '') + expires + '; path=/';
  }
}

customElements.define('country-redirect-popup', CountryRedirectPopup);
